import { Card, Skeleton, cn } from "ui";
import { SkeletonButton } from "../primitives/SkeletonButton";
import { SkeletonField } from "../primitives/SkeletonField";
import { SkeletonText } from "../primitives/SkeletonText";
import { SkeletonCircle } from "../primitives/SkeletonCircle";

type ConnectionIntegrationSkeletonProps = {
  isConnected?: boolean;
  className?: string;
};

export const ConnectionIntegrationSkeleton = ({
  isConnected = false,
  className,
}: ConnectionIntegrationSkeletonProps) => (
  <div className={cn("flex flex-col", className)}>
    {/* Breadcrumb */}
    <Skeleton className="h-4 w-56" />

    {/* Header: connection name + actions */}
    <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-9 w-64 rounded-md" />
      </div>
      <div className="flex gap-2">
        <SkeletonButton className="w-24" />
        <SkeletonButton className="w-28" />
      </div>
    </div>

    {isConnected ? (
      /* Connection details card */
      <div className="mt-4 flex flex-wrap items-center gap-6 rounded-lg border border-foundations-subtle p-4 sm:gap-14 sm:p-6">
        <Skeleton className="h-12 w-12 rounded-md" />
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>
    ) : (
      /* Configuration progress bar */
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-2 w-full rounded-full sm:w-80" />
      </div>
    )}

    {/* Form sections — connected: all open; new: only the first is open */}
    <div className="mt-6 flex flex-col gap-6">
      {Array.from({ length: 3 }).map((_, i) => {
        const isOpen = isConnected || i === 0;

        return (
          <Card key={i} disableHover className="overflow-hidden p-0">
            <div
              className={cn(
                "flex items-center justify-between p-6",
                isOpen && "border-b border-foundations-subtle"
              )}
            >
              <Skeleton className="h-4 w-40" />
              <div className="flex items-center gap-3">
                <Skeleton className="h-6 w-24 rounded-full" />
                <Skeleton className="h-5 w-5" />
              </div>
            </div>

            {isOpen && (
              <div className="grid grid-cols-1 divide-foundations-subtle md:grid-cols-2 md:divide-x">
                <div className="space-y-4 p-6">
                  <Skeleton className="h-4 w-32" />
                  {Array.from({ length: 3 }).map((_, index) => (
                    <SkeletonField key={index} />
                  ))}
                </div>

                <div className="flex flex-col gap-3 p-6">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <SkeletonCircle size={16} />
                    <Skeleton className="h-8 w-8 rounded-md" />
                  </div>
                  <Skeleton className="mt-1 h-3 w-3/4" />
                  <SkeletonText lines={2} className="h-3" />
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  </div>
);
